/**
 * Wishlist + Tell-a-Friend
 *
 * Wishlist: localStorage-backed array of product handles. No app, no
 * backend, no customer login required. Persists across sessions in the
 * same browser.
 *
 * Tell a friend: uses navigator.share() Web Share API on supported
 * browsers (mobile + modern desktop), falls back to a manual share
 * popover with social buttons + Copy link.
 *
 * Public API: window.fluffWishlist
 *   .getAll()           → array of handles
 *   .contains(handle)   → boolean
 *   .add(handle)        → void
 *   .remove(handle)     → void
 *   .toggle(handle)     → boolean (true if added, false if removed)
 *   .count()            → number
 *   .initButtons()      → re-bind toggle buttons (call after dynamic render)
 *
 * Custom event: 'wishlist:updated' is dispatched on document on every change.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'fluff_wishlist';

  // ===== Wishlist state =====
  var Wishlist = {
    getAll: function() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    save: function(handles) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
        document.dispatchEvent(new CustomEvent('wishlist:updated', {
          detail: { items: handles }
        }));
      } catch (e) {
        console.warn('Wishlist save failed:', e);
      }
    },
    contains: function(handle) {
      return this.getAll().indexOf(handle) !== -1;
    },
    add: function(handle) {
      var list = this.getAll();
      if (handle && list.indexOf(handle) === -1) {
        list.push(handle);
        this.save(list);
      }
    },
    remove: function(handle) {
      var list = this.getAll().filter(function(h) { return h !== handle; });
      this.save(list);
    },
    toggle: function(handle) {
      var willAdd = !this.contains(handle);
      if (willAdd) {
        this.add(handle);
      } else {
        this.remove(handle);
      }
      return willAdd;
    },
    count: function() {
      return this.getAll().length;
    }
  };

  // ===== Wishlist toggle buttons =====
  function refreshWishlistButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(function(btn) {
      var handle = btn.dataset.productHandle;
      var isActive = Wishlist.contains(handle);
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    refreshWishlistCount();
  }

  // ===== Wishlist count badge (in header, drawer, mobile bar) =====
  function refreshWishlistCount() {
    var count = Wishlist.count();
    var badges = document.querySelectorAll('[data-wishlist-count]');
    badges.forEach(function(badge) {
      // Format: data-wishlist-count="parens" wraps in (N), default is just N
      var format = badge.getAttribute('data-wishlist-count');
      if (format === 'parens') {
        badge.textContent = '(' + count + ')';
      } else {
        badge.textContent = count;
      }
      badge.hidden = count === 0;
    });
  }

  function bindWishlistButton(btn) {
    if (btn.__wishlistBound) return;
    btn.__wishlistBound = true;
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      var handle = btn.dataset.productHandle;
      if (!handle) return;
      var added = Wishlist.toggle(handle);
      refreshWishlistButtons();
      // If on the wishlist page and we just removed, drop the card
      if (!added) {
        var card = btn.closest('[data-wishlist-card]');
        if (card && card.parentNode) {
          card.parentNode.removeChild(card);
          // Show empty state if grid is now empty
          var grid = document.querySelector('[data-wishlist-grid]');
          var empty = document.querySelector('[data-wishlist-empty]');
          if (grid && empty && grid.children.length === 0) {
            empty.hidden = false;
          }
        }
      }
    });
  }

  function initWishlistButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(bindWishlistButton);
    refreshWishlistButtons();
  }

  // Sync state across tabs
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) refreshWishlistButtons();
  });

  // Expose public API
  Wishlist.initButtons = initWishlistButtons;
  window.fluffWishlist = Wishlist;

  // ===== Tell-a-Friend / Share =====
  function initTellAFriend() {
    var buttons = document.querySelectorAll('[data-tell-a-friend]');
    if (!buttons.length) return;
    var popover = document.querySelector('[data-share-popover]');

    buttons.forEach(function(btn) {
      if (btn.__tellAFriendBound) return;
      btn.__tellAFriendBound = true;
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var shareData = {
          title: btn.dataset.shareTitle || document.title,
          text: btn.dataset.shareText || '',
          url: btn.dataset.shareUrl || window.location.href
        };

        // Feature detection — only use native share if browser fully supports it
        var canNativeShare = (typeof navigator.share === 'function')
                          && (typeof navigator.canShare === 'function')
                          && navigator.canShare(shareData);

        if (canNativeShare) {
          navigator.share(shareData).catch(function(err) {
            // User cancellation is normal — no fallback needed
            if (err && err.name !== 'AbortError') {
              console.warn('Native share failed, opening fallback:', err);
              openSharePopover(popover, shareData);
            }
          });
        } else {
          openSharePopover(popover, shareData);
        }
      });
    });

    if (popover && !popover.__bound) {
      popover.__bound = true;
      var closers = popover.querySelectorAll('[data-share-close]');
      closers.forEach(function(el) {
        el.addEventListener('click', function() { popover.hidden = true; });
      });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !popover.hidden) popover.hidden = true;
      });
    }
  }

  function openSharePopover(popover, data) {
    if (!popover) return;
    var url = encodeURIComponent(data.url);
    var text = encodeURIComponent(data.title);
    var desc = encodeURIComponent(data.text);

    var fb = popover.querySelector('[data-share-facebook]');
    var tw = popover.querySelector('[data-share-twitter]');
    var pn = popover.querySelector('[data-share-pinterest]');
    var em = popover.querySelector('[data-share-email]');
    var cp = popover.querySelector('[data-share-copy]');
    var cpText = popover.querySelector('[data-copy-text]');

    if (fb) fb.href = 'https://www.facebook.com/sharer/sharer.php?u=' + url;
    if (tw) tw.href = 'https://twitter.com/intent/tweet?url=' + url + '&text=' + text;
    if (pn) pn.href = 'https://pinterest.com/pin/create/button/?url=' + url + '&description=' + text;
    if (em) em.href = 'mailto:?subject=' + text + '&body=' + desc + '%0A%0A' + url;

    if (cp) {
      cp.onclick = function() {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(data.url).then(function() {
            if (cpText) cpText.textContent = 'Copied!';
            setTimeout(function() {
              if (cpText) cpText.textContent = 'Copy link';
            }, 2000);
          }).catch(function() {
            window.prompt('Copy this link:', data.url);
          });
        } else {
          window.prompt('Copy this link:', data.url);
        }
      };
    }

    popover.hidden = false;
  }

  // ===== Init =====
  function init() {
    initWishlistButtons();
    initTellAFriend();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
